import { errorNotificationApi, silentApi } from '.'
import { I_CreateAdmin, I_CreatePlan, I_UpdatePlan } from '../interfaces'

export interface Company {
  _id?: string
  companyName?: string
  imageUrl?: string
  owner?: {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
  }
  planType?: string
  subscribedTeamSize?: number
  // Non-deleted members (seats in use), from the admin list and detail responses.
  memberCount?: number
  status?: string
  interval?: string
  isUpgraded?: boolean
  deleted?: boolean
  workType?: string
  teamSize?: number
  foundingYear?: string
  createdAt?: string
  amountPaid?: number
  subscriptionStartDate?: number
  subscriptionEndDate?: number
  nextCycle?: number
  cancelAt?: number | null
  canceledBy?: string
}

export const getAllAdmin = async () => {
  return await errorNotificationApi.get(`/admin/admin-list`)
}

export const createAdmin = async (data: I_CreateAdmin) => {
  return await errorNotificationApi.post(`/admin/add-admin`, data)
}

export const deleteAdmin = async (adminId: string) => {
  return await errorNotificationApi.delete(`/admin/delete-admin/${adminId}`)
}

export const permDeleteAdmin = async (adminId: string) => {
  return await errorNotificationApi.delete(`/admin/perm-delete/${adminId}`)
}

export const restoreAdmin = async (adminId: string) => {
  return await errorNotificationApi.patch(`/admin/restore-access/${adminId}`)
}

export const getAllCompanies = async (
  deleted: boolean,
  params?: { skip?: number; limit?: number; search?: string; status?: string }
) => {
  return await errorNotificationApi.get(`/admin/all-companies/deleted/${deleted}`, {
    params,
  })
}

export const restoreCompany = async (companyId: string) => {
  return await errorNotificationApi.patch(`/admin/restore-company/${companyId}`)
}

export const deleteCompany = async (companyId: string) => {
  return await errorNotificationApi.delete(`/admin/company/${companyId}`)
}

export const getAdminById = async (id: string) => {
  return await errorNotificationApi.get(`/admin/by-id/${id}`)
}

export const generateApiKey = async (userSecret: string) => {
  return await errorNotificationApi.patch(`/admin/generate-api-key/${userSecret}`)
}

export const permDeleteCompany = async (companyId: string[]) => {
  return await errorNotificationApi.delete(`/admin/company-permanently`, {
    data: { companyIds: companyId },
  })
}

export const getCompanyById = async (companyId: string) => {
  return await errorNotificationApi.get(`/admin/company-by-id/companyId/${companyId}`)
}

// ------------------------------------plans----------------------------------------

export const getAllPlans = async () => {
  return await errorNotificationApi.get(`/admin/fetch-plans`)
}

export const getPlanById = async (planId: string) => {
  return await errorNotificationApi.get(`/admin/fetch-plan/${planId}`)
}

export const createPlan = async (data: I_CreatePlan) => {
  return await errorNotificationApi.post(`/admin/create-plan`, data)
}

export const updatePlan = async (data: I_UpdatePlan, planId: string) => {
  return await errorNotificationApi.put(`/admin/updatePlan/${planId}`, data)
}

// ------------------------------------plans----------------------------------------

export const getCompanyAnalytics = async (companyId: string) => {
  return await errorNotificationApi.get(`/admin/company-analytics/companyId/${companyId}`)
}

export const getExtraUser = async () => {
  return await errorNotificationApi.get(`/admin/extra-user-amount`)
}

export const updateExtraUser = async (data: {
  extraUserChargeMonthly?: number
  extraUserChargeYearly?: number
  baseUserCount?: number
}) => {
  return await errorNotificationApi.put(`/admin/extra-user-amount`, data)
}

export const updateNotificationEmail = async (data: {
  notificationEmail: string
  subjectLine?: string
  messageBody?: string
}) => {
  return await errorNotificationApi.put(`/admin/notification-email`, data)
}

export const getCompanyInvoice = async (companyId: string) => {
  return await errorNotificationApi.get(`/admin/company-invoices/${companyId}`)
}

// NOTE: the support-ticket axios helpers that used to live here were removed
// (NHR-2866). They were imported nowhere and serialized `status` as
// `status=[1,2]` while the live RTK Query path sends `status=1,2` — a
// correctness trap for anyone who reused them. Support requests go through
// `src/store/api/adminApi.ts`.

// ------------------------------------admin tenant login---------------------------------
// Silent: the Log in as dialog and the audit page draw every failure themselves (a toast sits behind an open
// dialog). Bodies stay `unknown` until `src/logic/interfaces/adminImpersonation.ts` normalises them.

export const getImpersonationMembers = async (
  companyId: string,
  params: { cursor?: string; limit?: number; search?: string }
): Promise<unknown> => {
  return await silentApi.get(`/admin/impersonation/companies/${companyId}/members`, { params })
}

export const startImpersonation = async (data: {
  companyId: string
  memberId: string
  reason: string
}): Promise<unknown> => {
  return await silentApi.post(`/admin/impersonation/start`, data)
}

export const getImpersonationAudit = async (params: Record<string, string | number | boolean>): Promise<unknown> => {
  return await silentApi.get(`/admin/impersonation/audit`, { params })
}
